import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FlashMensagem, FlashTipo } from '../components/models/flash-mensagem.model';

/**
 * Mensagens globais (sucesso / aviso / erro). Suporta **múltiplas mensagens
 * simultâneas** empilhadas: cada chamada a `sucesso()` / `aviso()` / `erro()`
 * **adiciona** uma nova mensagem em vez de substituir a anterior.
 *
 * O componente {@link FlashMessageComponent} renderiza a lista atual com
 * `*ngFor` e botão de fechar individual. Cada mensagem tem auto-limpeza
 * própria (timer por id). Use {@link limpar} ao mudar filtro/paginação na
 * listagem para descartar todas de uma vez.
 */
@Injectable({
  providedIn: 'root',
})
export class FlashMessageService {
  private readonly mensagensSubject = new BehaviorSubject<FlashMensagem[]>([]);

  /** Lista atual de mensagens (na ordem em que foram adicionadas). */
  readonly mensagens$ = this.mensagensSubject.asObservable();

  private nextId = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** Tempo até remover automaticamente cada mensagem (ms). */
  private readonly duracaoAutoLimparMs = 6000;

  sucesso(texto: string): void {
    this.adicionar({ tipo: 'success', texto });
  }

  aviso(texto: string): void {
    this.adicionar({ tipo: 'warning', texto });
  }

  erro(texto: string): void {
    this.adicionar({ tipo: 'error', texto });
  }

  /** Para testes ou cenários especiais. */
  mostrarTipo(tipo: FlashTipo, texto: string): void {
    this.adicionar({ tipo, texto });
  }

  /** Remove todas as mensagens e cancela seus timers. */
  limpar(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    this.mensagensSubject.next([]);
  }

  /** Remove uma mensagem específica (acionado pelo botão fechar do componente). */
  limparPorId(id: number): void {
    const t = this.timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(id);
    }
    const atuais = this.mensagensSubject.value.filter((m) => m.id !== id);
    if (atuais.length !== this.mensagensSubject.value.length) {
      this.mensagensSubject.next(atuais);
    }
  }

  private adicionar(parcial: { tipo: FlashTipo; texto: string }): void {
    const m: FlashMensagem = { id: this.nextId++, ...parcial };
    this.mensagensSubject.next([...this.mensagensSubject.value, m]);
    const timer = setTimeout(() => this.limparPorId(m.id), this.duracaoAutoLimparMs);
    this.timers.set(m.id, timer);
  }
}
